import { AnimatePresence, m } from 'framer-motion';
import { Fragment, useEffect, useState } from 'react';
import { opacityAnimation } from '@ui/animation/opacity-animation';
import { Skeleton } from '@ui/skeleton/skeleton';
import { useProducts } from '@common/billing/pricing-table/use-products';
import { Product } from '@common/billing/product';
import {
  findBestPrice,
  UpsellBillingCycle,
} from '@common/billing/pricing-table/find-best-price';
import { useAuth } from '@common/auth/use-auth';
import clsx from 'clsx';
import { Chip } from '@ui/forms/input-field/chip-field/chip';
import { Trans } from '@ui/i18n/trans';
import { FormattedPrice } from '@common/billing/formatted-price';
import { Button } from '@ui/buttons/button';
import { Link } from 'react-router-dom';
import { setInLocalStorage } from '@ui/utils/hooks/local-storage';
import { ProductFeatureList } from '@common/billing/pricing-table/product-feature-list';
import axios from 'axios';

interface PricingTableProps {
  selectedCycle: UpsellBillingCycle;
  className?: string;
  productLoader?: string;
  filter?: boolean;
  business?: boolean;
}
export function PricingTable({
  selectedCycle,
  className,
  productLoader,
  business,
  filter
}: PricingTableProps) {
  const data = useProducts(productLoader);
  const [filteredProducts, setFilteredProducts] = useState(data?.data?.products || []);

  useEffect(() => {
    if (!data?.data?.products) return; // Ensure products exist

    let filtered = data.data.products;
    if (filter) {
      filtered = business
        ? filtered.filter(prod => prod.company)
        : filtered.filter(prod => !prod.company);
    }

    setFilteredProducts(filtered);
  }, [data.status, filter, business]);

  return (
    <div
      className={clsx(
        'flex flex-col items-stretch gap-24 overflow-x-auto overflow-y-visible pb-20 md:flex-row md:flex-wrap md:justify-center w-full',
        className,
      )}
    >
      <AnimatePresence initial={false} mode="wait">
        {data?.data ? (
          <PlanList
            key="plan-list"
            plans={filteredProducts}
            selectedPeriod={selectedCycle}
          />
        ) : (
          <SkeletonLoader key="skeleton-loader" />
        )}
      </AnimatePresence>
    </div>
  );
}

interface PlanListProps {
  plans: Product[];
  selectedPeriod: UpsellBillingCycle;
}
function PlanList({ plans, selectedPeriod }: PlanListProps) {
  const { isLoggedIn, isSubscribed, user, getPlan, getPermission } = useAuth();
  let currentPlan = getPlan();




  const filteredPlans = plans.filter(plan => !plan.hidden);
  return (
    <Fragment>
      {filteredPlans.map((plan, index) => {
        const isFirst = index === 0;
        const isLast = index === filteredPlans.length - 1;
        const price = findBestPrice(selectedPeriod, plan.prices);
        const yearlyPrice = plan.prices ? findBestPrice('yearly', plan.prices) : null;
        const monthlyPrice = plan.prices ? findBestPrice('monthly', plan.prices) : null;

        let upgradeRoute;
        if (!isLoggedIn) {
          upgradeRoute = `/register?redirectFrom=pricing`;
        }
        if (isSubscribed) {
          upgradeRoute = `/change-plan/${plan.id}/${price?.id}/confirm`;
        }
        if (isLoggedIn && !plan.free) {
          upgradeRoute = `/checkout/${plan.id}/${price?.id}`;
        }

        return (
          <m.div
            key={plan.id}
            {...opacityAnimation}
            className={clsx(
              'w-full dark:border-2 bg-white dark:bg px-28 py-28 rounded-[16px] md:min-w-240 md:max-w-350',
              // isFirst && 'ml-auto',
              // isLast && 'mr-auto',
              'flex flex-col justify-between'
            )}
          >
            <div className="mb-32">
              {/* <Chip
                radius="rounded"
                size="sm"
                className={clsx(
                  'mb-20 w-min',
                  !plan.recommended && 'invisible',
                  'plans-card-badge'
                )}
              >
                <Trans message="Most popular" />
              </Chip> */}

              <div className='w-full flex items-center flex-wrap gap-10'>
                {plan.recommended ? <div className="plans-card-badge mb-16"><Trans message='Most popular' /></div> : ''}
                {plan.company ? <div className="plans-card-badge mb-16"><Trans message='Business' /></div> : ''}
              </div>
              <div className="mb-4 text-xl text-black dark:text-white font-semibold">
                <Trans message={plan.name} />
              </div>
              <div className="text-sm text-muted">
                <Trans message={plan.description} />
              </div>
            </div>
            <div>
              {price ? (
                <FormattedPrice
                  priceClassName="font-bold text-4xl"
                  periodClassName="text-muted text-xs"
                  variant="separateLine"
                  price={price}
                />
              ) : (
                <div className="text-4xl font-bold">
                  <Trans message="Free" />
                </div>
              )}
              {
                yearlyPrice?.amount && monthlyPrice?.amount &&
                monthlyPrice.amount * 12 > yearlyPrice.amount && (
                  <p className='text-primary dark:text-white mt-10 text-sm'>
                    <span className='font-bold'>Exclusive Deal!</span> – Get 
                    <span className='font-bold text-gradient-pink'> ${parseFloat((monthlyPrice.amount * 12 - yearlyPrice.amount).toFixed(2))} OFF </span>  
                    with a Yearly Subscription!
                  </p>
      )}
              <hr className='mt-20' />
              <div className="mt-20">
                <Button
                  variant='flat'
                  // variant={plan.recommended ? 'flat' : 'outline'}
                  color="primary"
                  className="w-full rounded-[10px] overflow-hidden dark:text-white hover:bg-[#0154A0] dark:hover:bg-[#0154A0]"
                  size="md"
                  elementType={upgradeRoute ? Link : undefined}
                  disabled={!upgradeRoute || currentPlan?.id == plan.id}
                  onClick={() => {
                    if (isLoggedIn || !price || !plan) return;
                    setInLocalStorage('be.onboarding.selected', {
                      productId: plan.id,
                      priceId: price.id,
                    });
                  }}
                  to={upgradeRoute}
                >

                  {plan.free ? (
                    <Trans message="Get started" />
                  ) : (
                    <Trans message="Upgrade" />
                  )}
                </Button>
              </div>
              <ProductFeatureList product={plan} />
            </div>
          </m.div>
        );
      })}
    </Fragment>
  );
}

function SkeletonLoader() {
  return (
    <Fragment>
      <PlanSkeleton key="skeleton-1" />
      <PlanSkeleton key="skeleton-2" />
      <PlanSkeleton key="skeleton-3" />
    </Fragment>
  );
}

function PlanSkeleton() {
  return (
    <m.div
      {...opacityAnimation}
      className="w-full rounded-lg border px-28 py-90 shadow-lg md:max-w-350"
    >
      <Skeleton className="my-10" />
      <Skeleton className="mb-40" />
      <Skeleton className="mb-40 h-30" />
      <Skeleton className="mb-40 h-40" />
      <Skeleton className="mb-20" />
      <Skeleton />
      <Skeleton />
    </m.div>
  );
}
